import React, { createContext, useState, useEffect } from "react";
import { Flex, Text, Button, ModalBody, ModalHeader, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { colours } from "@/utils/themeUtils";
import { ClasswizeModal } from "@/components/ui/Modal";
import { default as EmptyInbox } from "@/assets/emptyInbox.svg";
import { ScheduleTable } from "./ScheduleTable";
import { useSchedulesQuery } from "@/query/scheduleQuery";
import { ScheduleRule } from "./ScheduleTableRow";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { envConfig } from "@/config";
import { Notice } from "@/components/Notice";
import { getClassroomDisplayName } from "@/utils/classroomUtils";

interface Props {
	isOpen: boolean;
	onClose: () => void;

	classroom: ClassroomOverviewDTO;
}

interface ScheduleTableContextType {
	showNewScheduleRow: boolean;
	setShowNewScheduleRow: (show: boolean) => void;
	duplicateSchedule: ScheduleRule | undefined;
	setDuplicateSchedule: (schedule: ScheduleRule | undefined) => void;
	rowsInEditMode: Set<number>;
	registerRowInEditMode: (rowId: number) => void;
	unregisterRowInEditMode: (rowId: number) => void;
	handleNewScheduleRule: (schedule?: ScheduleRule) => void;
	exceedsStudentLimit: boolean;
}

export const ScheduleTableContext = createContext<ScheduleTableContextType>({
	showNewScheduleRow: false,
	setShowNewScheduleRow: () => {},
	duplicateSchedule: undefined,
	setDuplicateSchedule: () => {},
	rowsInEditMode: new Set(),
	registerRowInEditMode: () => {},
	unregisterRowInEditMode: () => {},
	handleNewScheduleRule: () => {},
	exceedsStudentLimit: false,
});

export function SchedulingModal(props: Props): JSX.Element {
	const { t } = useTranslation();
	const [showNewScheduleRow, setShowNewScheduleRow] = useState(false);
	const [duplicateSchedule, setDuplicateSchedule] = useState<ScheduleRule | undefined>(undefined);
	const [rowsInEditMode, setRowsInEditMode] = useState<Set<number>>(new Set());

	const userSessionStore = useUserSessionStore();
	const schedulesQuery = useSchedulesQuery(props.classroom.stableIdV1, userSessionStore.getActiveUser() ?? "", props.isOpen);

	const isEmpty = (schedulesQuery.data ?? []).length === 0 && !showNewScheduleRow;
	const maxStudents = envConfig.MAX_STUDENTS;
	const exceedsStudentLimit = props.classroom.memberCount > maxStudents;

	// Reset all unsaved state when modal is closed
	useEffect(() => {
		if (!props.isOpen) {
			setShowNewScheduleRow(false);
			setDuplicateSchedule(undefined);
			setRowsInEditMode(new Set());
		}
	}, [props.isOpen]);

	/** Shows the "new schedule" row, optionally with the details of an existing schedule */
	function handleNewScheduleRule(schedule?: ScheduleRule) {
		let newSchedule: ScheduleRule | undefined;
		if (schedule) {
			newSchedule = { ...schedule, active: false };
		}
		setDuplicateSchedule(newSchedule);
		setShowNewScheduleRow(true);
	}

	function registerRowInEditMode(rowId: number) {
		setRowsInEditMode((prev) => new Set(prev).add(rowId));
	}

	function unregisterRowInEditMode(rowId: number) {
		setRowsInEditMode((prev) => {
			const newSet = new Set(prev);
			newSet.delete(rowId);
			return newSet;
		});
	}

	return (
		<ClasswizeModal
			isOpen={props.isOpen}
			onClose={props.onClose}
			size={"6xl"}
			maxW={"80rem"}
			title={t("scheduling.title", { className: getClassroomDisplayName(props.classroom) })}
			dataTestId={"scheduling-modal"}
			minHeight={"600px"}
			maxHeight={"600px"}
		>
			<ScheduleTableContext.Provider
				value={{
					showNewScheduleRow,
					setShowNewScheduleRow,
					duplicateSchedule,
					setDuplicateSchedule,
					rowsInEditMode,
					registerRowInEditMode,
					unregisterRowInEditMode,
					handleNewScheduleRule,
					exceedsStudentLimit,
				}}
			>
				<ModalHeader>
					<Text fontSize={"14px"} color={colours.navy[800]} lineHeight={"20px"} fontWeight={400} mb={4}>
						{t("scheduling.intro")}
					</Text>

					{exceedsStudentLimit && (
						<Notice status="error" mb={4}>
							<Text marginTop={1} fontSize="14px" fontWeight="500">
								{t("scheduling.studentLimitNotice", { memberCount: props.classroom.memberCount, maxStudents })}
							</Text>
						</Notice>
					)}

					{!schedulesQuery.isLoading && !isEmpty && (
						<Flex justifyContent="flex-end" alignItems="center" mt={6}>
							<Button
								onClick={() => handleNewScheduleRule()}
								isDisabled={showNewScheduleRow || rowsInEditMode.size > 0 || exceedsStudentLimit}
							>
								<ChakraFAIcon icon="fa-plus" variant="solid" mr={2} boxSize={4} />
								{t("scheduling.addNewSchedule")}
							</Button>
						</Flex>
					)}
				</ModalHeader>
				<ModalBody>
					<Flex direction={"column"}>
						{schedulesQuery.isLoading && (
							<Flex my={50} gap={5} flexDirection={"column"} alignItems={"center"}>
								<Spinner size="xl" />
							</Flex>
						)}
						{!schedulesQuery.isLoading && isEmpty ? (
							<Flex my={50} gap={5} flexDirection={"column"} alignItems={"center"}>
								<EmptyInbox />
								<Text fontSize={"14px"} color={colours.navy[800]} lineHeight={"20px"}>
									{t("scheduling.emptyState")}
								</Text>
								<Button onClick={() => handleNewScheduleRule()} isDisabled={exceedsStudentLimit}>
									<ChakraFAIcon icon="fa-calendar" variant="solid" mr={2} boxSize={4} />
									{t("scheduling.createSchedule")}
								</Button>
							</Flex>
						) : (
							<ScheduleTable
								classroomId={props.classroom.stableIdV1}
								scheduleRules={schedulesQuery.data ?? []}
								className={getClassroomDisplayName(props.classroom)}
							/>
						)}
					</Flex>
				</ModalBody>
			</ScheduleTableContext.Provider>
		</ClasswizeModal>
	);
}
